import React from 'react'
import ReactDOM from 'react-dom'
import { init, loadRemote } from '@module-federation/runtime'

let RemoteApp = null
let isInitialized = false
let styleObserver = null
let activeContainerId = null

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return React.createElement(
        'div',
        { 
          style: { 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '20px'
          } 
        },
        React.createElement('h3', { style: { color: 'red' } }, 'Error loading microfrontend'),
        React.createElement('pre', { style: { fontSize: '12px', maxWidth: '600px', overflow: 'auto' } }, 
          this.state.error ? this.state.error.toString() : 'Unknown error'
        )
      )
    }

    return this.props.children
  }
}

const scopeCssSelectors = (cssText, containerId) => {
  const scopePrefix = `#${containerId}`
  
  return cssText.replace(
    /([^{}]+)(\{[^{}]*\})/g,
    (match, selectors, rules) => {
      const scopedSelectors = selectors
        .split(',')
        .map(selector => {
          selector = selector.trim()
          if (!selector) return selector
          
          // Skip @rules (media queries, keyframes, font-face, etc.)
          if (selector.startsWith('@')) return selector
          
          // Skip :root, html, body - scope them to container instead
          if (selector === ':root' || selector === 'html' || selector === 'body') {
            return scopePrefix
          }
          
          // Skip selectors that already have the scope
          if (selector.startsWith(scopePrefix)) return selector
          
          // Handle * selector
          if (selector === '*') {
            return `${scopePrefix} *`
          }
          
          return `${scopePrefix} ${selector}`
        })
        .join(', ')
      
      return `${scopedSelectors} ${rules}`
    }
  )
}

// Detect vendor CSS that must remain global (do NOT scope)
const isVendorPrimeStyle = (node) => {
  if (node.tagName === 'LINK' && node.rel === 'stylesheet') {
    const href = node.href || ''
    // Common vendor assets for PrimeReact ecosystem
    // - primereact base/styles
    // - primeicons
    // - primeflex
    return /primereact|primeicons|primeflex/.test(href)
  }
  return false
}

const isMfeStyle = (node, appUrl) => {
  // Skip scoping for vendor Prime* styles to keep them global
  if (isVendorPrimeStyle(node)) return false
  if (node.tagName === 'STYLE') {
    const dataAttr = node.getAttribute('data-vite-dev-id') || ''
    return dataAttr.includes('/interactions/') || 
           dataAttr.includes(appUrl) ||
           node.hasAttribute('data-mfe-interactions')
  }
  if (node.tagName === 'LINK' && node.rel === 'stylesheet') {
    const href = node.href || ''
    return href.includes('/interactions/assets/') || 
           (appUrl && href.includes(appUrl) && href.includes('/interactions/'))
  }
  return false
}

const scopeStyleElement = (styleNode, containerId) => {
  if (styleNode.tagName === 'STYLE' && styleNode.textContent) {
    const originalCss = styleNode.textContent
    // If this style block appears to be PrimeReact/PrimeIcons, skip scoping to keep globals intact
    const isPrimeCss = /\.p-(component|menu|menubar|tieredmenu|contextmenu|dropdown|overlay|dialog|button|input|checkbox|radiobutton|tooltip|datatable|tree|sidebar|toast|confirmdialog|overlaypanel|multiselect|calendar|chips|tag|badge|listbox|panelmenu|tabview|toolbar|card|avatar|chip)\b/i.test(originalCss)
    if (isPrimeCss) {
      styleNode.setAttribute('data-mfe-scoped', 'true')
      return
    }
    const scopedCss = scopeCssSelectors(originalCss, containerId)
    styleNode.textContent = scopedCss
    styleNode.setAttribute('data-mfe-scoped', 'true')
  }
}

const scopeLinkElement = async (linkNode, containerId) => {
  if (linkNode.tagName === 'LINK' && linkNode.rel === 'stylesheet') {
    try {
      const response = await fetch(linkNode.href)
      const cssText = await response.text()
      // If this stylesheet looks like PrimeReact/PrimeIcons, do not scope; insert as-is
      const isPrimeCss = /\.p-(component|menu|menubar|tieredmenu|contextmenu|dropdown|overlay|dialog|button|input|checkbox|radiobutton|tooltip|datatable|tree|sidebar|toast|confirmdialog|overlaypanel|multiselect|calendar|chips|tag|badge|listbox|panelmenu|tabview|toolbar|card|avatar|chip)\b/i.test(cssText)
      const scopedCss = isPrimeCss ? cssText : scopeCssSelectors(cssText, containerId)
      
      const styleElement = document.createElement('style')
      styleElement.textContent = scopedCss
      styleElement.setAttribute('data-mfe-scoped', 'true')
      styleElement.setAttribute('data-original-href', linkNode.href)
      
      linkNode.parentNode.insertBefore(styleElement, linkNode)
      linkNode.remove()
    } catch (err) {
      console.warn('Failed to scope CSS link:', linkNode.href, err)
    }
  }
}

const startStyleObserver = (containerId, appUrl) => {
  if (styleObserver) {
    styleObserver.disconnect()
  }
  
  activeContainerId = containerId
  
  styleObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && activeContainerId) {
          if (isMfeStyle(node, appUrl) && !node.hasAttribute('data-mfe-scoped')) {
            if (node.tagName === 'STYLE') {
              scopeStyleElement(node, activeContainerId)
            } else if (node.tagName === 'LINK') {
              scopeLinkElement(node, activeContainerId)
            }
          }
        }
      })
    })
  })
  
  styleObserver.observe(document.head, {
    childList: true,
    subtree: true
  })
}

const stopStyleObserver = () => {
  if (styleObserver) {
    styleObserver.disconnect()
    styleObserver = null
  }
  activeContainerId = null
}

const scopeExistingMfeStyles = (containerId, appUrl) => {
  const styles = document.head.querySelectorAll('style, link[rel="stylesheet"]')
  styles.forEach(style => {
    if (isMfeStyle(style, appUrl) && !style.hasAttribute('data-mfe-scoped')) {
      if (style.tagName === 'STYLE') {
        scopeStyleElement(style, containerId)
      } else if (style.tagName === 'LINK') {
        scopeLinkElement(style, containerId)
      }
    }
  })
}

const initializeRemoteApp = (appUrl) => {
  if (!isInitialized) {
    try {
      init({
        name: 'host',
        remotes: [
          {
            name: 'interactions',
            alias: 'interactions',
            entry: `${appUrl}/interactions/assets/remoteEntry.js`,
            type: 'module',
          },
        ],
        shared: {
          react: {
            version: '17.0.2',
            scope: 'default',
            lib: () => React,
            shareConfig: {
              singleton: true,
              requiredVersion: '^17.0.0',
            },
          },
          'react-dom': {
            version: '17.0.2',
            scope: 'default',
            lib: () => ReactDOM,
            shareConfig: {
              singleton: true,
              requiredVersion: '^17.0.0',
            },
          },
        },
      })
      isInitialized = true
    } catch (e) {
      console.error('Error initializing module federation', e)
    }
  }

  if (!RemoteApp && isInitialized) {
    RemoteApp = React.lazy(() => loadRemote('interactions/App'))
  }

  return RemoteApp
}

export const mountInteractionsApp = (containerId, props) => {
  const { 
    appUrl, 
    apiUrl, 
    authHost,
    sessionId,
    ciToken,
    userContext, 
    language,
    playerFeatures,
    contactId // TODO: Remove this if use iframe version
  } = props

  const RemoteAppComponent = initializeRemoteApp(appUrl)
  if (!RemoteAppComponent) {
    console.error('Failed to initialize remote app')
    return null
  }

  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id ${containerId} not found`)
    return null
  }

  // Start observing for MFE styles and scope them
  startStyleObserver(containerId, appUrl)
  scopeExistingMfeStyles(containerId, appUrl)

  const userContextWrapper = {
    ...userContext,
    sessionId: sessionId,
    authenticationSource: 'ci'
  }

  const AppWrapper = () => {
    return React.createElement(
      ErrorBoundary,
      null,
      React.createElement(
        React.Suspense,
        { 
          fallback: React.createElement(
            'div',
            { 
              style: { 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              } 
            },
            React.createElement('div', { style: { fontSize: '16px' } }, 'Loading...')
          )
        },

        React.createElement(RemoteAppComponent, {
          apiUrl,
          authHost,
          userContext: userContextWrapper,
          language,
          ciToken,
          playerFeatures,
          contactId
        })
      )
    )
  }

  try {
    ReactDOM.render(React.createElement(AppWrapper), container)
    console.log('React app rendered successfully')
  } catch (error) {
    console.error('Error rendering React app:', error)
    return null
  }

  return () => {
    stopStyleObserver()
    ReactDOM.unmountComponentAtNode(container)
  }
}

export const unmountInteractionsApp = (containerId) => {
  stopStyleObserver()
  const container = document.getElementById(containerId)
  if (container) {
    ReactDOM.unmountComponentAtNode(container)
  }
}
